import { useEffect, useState } from 'react';
import LoadingSkeleton from 'components/loading-skeleton';
import CommercialFooter from './exp/commercial/commercialFooter';
import CommercialFooterV2 from './exp/commercial/commercialFooterV2';
import ExpRealtyFooter from './exp/realty/expRealtyFooter';
import ZoocasaFooter from './zoocasa/zoocasaFooter';
import {
  EXP_COMMERCIAL_CA_TENANT_ID,
  EXP_COMMERCIAL_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US_TENANT_ID,
  getTenantById,
  TenantId,
  ZOOCASA_TENANT_ID,
} from '@tenants';
import { getTenantFooterConfig, getTenantOrganizationConfig } from '@tenants/utils/configs';
import { FeaturesType } from 'contexts/features';
import { CountryCode } from 'types/countries';
import ExpFooter from './exp/generic/expFooter';
import { Route } from 'components/dynamic-page/route-matchers';
import { LOCALE_EXP_COMMERCIAL_PATHS } from 'types/locales';
import { isTenantExpCommercial, isTenantExpCommercialCa } from 'tenants';
export const DEFAULT_SKELETON_HEIGHT = '15.7em';

export type FooterProps = {
  /** Determines if the accessibility widget should be shown */
  canShowAccessibilityWidget: boolean;

  /** The ID of the tenant to render the footer for */
  tenantId: TenantId;

  /** The country code of the tenant to render the footer for */
  countryCode: CountryCode;

  /** The route of the tenant to render the footer for */
  route: Route; // FIXME: think of a better way to handle route specific logic
};

export type DebugFooterProps = {
  /** The features to render the footer for */
  features?: FeaturesType;
};

export type FooterSkeletonProps = {
  /** The height of the skeleton */
  height: React.CSSProperties['height'];
};

/**
 * A component that displays a loading skeleton for the themed footer with a shimmering animation effect.
 * The skeleton appears as a gradient background that animates from left to right,
 * creating a subtle loading indicator. The gradient transitions between different
 * shades of grey (grey-2 and grey-3) in a continuous loop.
 *
 * @param height The height of the skeleton. Default is {@link DEFAULT_SKELETON_HEIGHT 15.7em}.
 */
export const FooterSkeleton = ({ height = DEFAULT_SKELETON_HEIGHT }: FooterSkeletonProps) => <LoadingSkeleton height={height} />;

export default function Footer({ canShowAccessibilityWidget, tenantId, features, countryCode, route }: FooterProps & DebugFooterProps) {
  const tenant = getTenantById(tenantId);
  const footerConfig = getTenantFooterConfig(tenant);
  const organizationConfig = getTenantOrganizationConfig(tenant);
  const [useNewExpCommercialFooter, setUseNewExpCommercialFooter] = useState(undefined);

  // TODO: cleanup this logic when we go live with expCommercial
  useEffect(() => {
    if (isTenantExpCommercial(tenantId) || isTenantExpCommercialCa(tenantId)) {
      const { hostname } = window.location;
      if (hostname !== 'legacy.expcommercial.com') {
        setUseNewExpCommercialFooter(true);
        return;
      }
      if (footerConfig?.footerColumns) {
        setUseNewExpCommercialFooter(false);
        const { column1, column2 } = footerConfig?.footerColumns ?? {};
        if (column1) {
          Object.assign(column1[0], {
            link: '/services',
          });
          Object.assign(column1[1], {
            label: {
              key: LOCALE_EXP_COMMERCIAL_PATHS.FOOTER_FINDEXPERT_LABEL,
            },
          });
          Object.assign(column1[3], {
            link: '/become-an-advisor',
          });
        }
        if (column2) {
          Object.assign(column2[0], {
            link: '/about',
          });
        }
      }
    }
  }, [tenantId, footerConfig]);

  switch (tenantId) {
    case EXP_REALTY_CA_TENANT_ID:
    case EXP_REALTY_US_TENANT_ID:
      return <ExpRealtyFooter config={footerConfig} organizationConfig={organizationConfig} tenantId={tenantId} features={features} countryCode={countryCode} />;
    case EXP_IN_SERVICE_TENANT_ID:
      return <ExpFooter config={footerConfig} organizationConfig={organizationConfig} countryCode={countryCode} testId={`${EXP_IN_SERVICE_TENANT_ID}-footer`} />;
    case EXP_COMMERCIAL_TENANT_ID:
    case EXP_COMMERCIAL_CA_TENANT_ID:
      if (useNewExpCommercialFooter === undefined) {
        return null;
      }
      return useNewExpCommercialFooter ?
        <CommercialFooterV2 config={footerConfig} organizationConfig={organizationConfig} /> :
        <CommercialFooter config={footerConfig} organizationConfig={organizationConfig} />;
    case ZOOCASA_TENANT_ID:
      return <ZoocasaFooter canShowAccessibilityWidget={canShowAccessibilityWidget} footerConfig={footerConfig} organizationConfig={organizationConfig} features={features} route={route} countryCode={countryCode} />;
    default:
      return null;
  }
}
